import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  Link,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

import { fetchPlayerRanking } from './RankingApi.jsx';
import './RankingWidget.css';

const getRankingAvatar = (item) => {
  if (item?.player?.photoUrl) {
    return {
      src: item.player.photoUrl,
      alt: item.player?.displayName || 'Joueur',
      variant: 'player',
    };
  }

  if (item?.team?.logoUrl) {
    return {
      src: item.team.logoUrl,
      alt: item.team?.name || item.team?.fullName || 'Équipe',
      variant: 'team',
    };
  }

  return {
    src: '',
    alt: item?.player?.displayName || 'Joueur',
    variant: 'fallback',
  };
};

const buildPlayerUrl = (player) => {
  if (!player?._id) return null;

  const playerLabel =
    player?.displayName ||
    [player?.firstname, player?.lastname].filter(Boolean).join(' ') ||
    player?._id;

  return `/dsas-view/joueur/${encodeURIComponent(playerLabel)}_${player._id}`;
};

const buildTeamUrl = (team) => {
  if (!team?._id) return null;

  const teamLabel = team?.name || team?.fullName || team?._id;

  return `/dsas-view/equipe/${encodeURIComponent(teamLabel)}_${team._id}`;
};

const formatDecimal = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';

  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

const getColumnsByStatType = (statType) => {
  switch (statType) {
    case 'tries':
      return {
        mainLabel: 'essais',
        ratioLabel: 'essais / match',
      };
    case 'scorers':
      return {
        mainLabel: 'points',
        ratioLabel: 'points / match',
      };
    case 'goals':
      return {
        mainLabel: 'buts',
        ratioLabel: 'buts / match',
      };
    case 'assists':
      return {
        mainLabel: 'passes',
        ratioLabel: 'passes / match',
      };
    case 'stars':
    default:
      return {
        mainLabel: 'étoiles',
        ratioLabel: 'étoiles / match',
      };
  }
};

export default function RankingSection({
  statType,
  discipline,
  competitionIds = [],
  season,
  widgetConfigurationId = null,
  token,
  baseUrl = '',
  limit = 20,
  limitOptions = [10, 20, 50, 100],
  sort = 'value_desc',
  onNavigate,
  showLimitSelector = false,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(limit);
  const [data, setData] = useState({
    items: [],
    total: 0,
    totalPages: 0,
    page: 1,
    limit,
  });

  const labels = useMemo(() => getColumnsByStatType(statType), [statType]);
  const competitionKey = competitionIds?.join(',') || '';

  useEffect(() => {
    setRowsPerPage(limit);
    setPage(1);
  }, [limit]);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const result = await fetchPlayerRanking({
          discipline,
          statType,
          competitionIds,
          season,
          widgetConfigurationId,
          page,
          limit: rowsPerPage,
          sort,
        });

        if (!ignore) {
          setData(result || { items: [], total: 0, totalPages: 0, page: 1, limit: rowsPerPage });
        }
      } catch (e) {
        if (!ignore) {
          setError('Impossible de charger le classement.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [
    page,
    discipline,
    statType,
    competitionKey,
    season,
    widgetConfigurationId,
    rowsPerPage,
    sort,
    baseUrl,
    token,
  ]);

  useEffect(() => {
    setPage(1);
  }, [statType, discipline, season, competitionKey, widgetConfigurationId]);

  const handleRowsPerPageChange = (event) => {
    const nextValue = Number(event.target.value);
    setRowsPerPage(nextValue);
    setPage(1);
  };

  const handleLinkClick = (event, url) => {
    if (!url || !onNavigate) return;
    event.preventDefault();
    onNavigate(url);
  };

  const renderPlayerName = (item) => {
    const playerUrl = buildPlayerUrl(item.player);
    const label = item.player?.displayName || '-';

    if (!playerUrl) return label;

    return (
      <Link
        href={playerUrl}
        underline="none"
        color="inherit"
        onClick={(event) => handleLinkClick(event, playerUrl)}
      >
        {label}
      </Link>
    );
  };

  const renderTeamName = (item) => {
    const teamUrl = buildTeamUrl(item.team);
    const label = item.team?.name || item.team?.fullName || '-';

    if (!teamUrl) return label;

    return (
      <Link
        href={teamUrl}
        underline="none"
        color="inherit"
        onClick={(event) => handleLinkClick(event, teamUrl)}
      >
        {label}
      </Link>
    );
  };

  const renderTable = () => (
    <TableContainer className="player-ranking-table__container">
      <Table size="small" className="player-ranking-table" aria-label="Classement des statistiques joueurs">
        <colgroup>
          <col className="player-ranking-table__col-rank" />
          <col className="player-ranking-table__col-avatar" />
          <col className="player-ranking-table__col-player" />
          <col className="player-ranking-table__col-value" />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell className="player-ranking-table__head-cell player-ranking-table__rank-cell">#</TableCell>
            <TableCell className="player-ranking-table__head-cell player-ranking-table__avatar-cell" aria-label="Logo ou photo" />
            <TableCell className="player-ranking-table__head-cell">Joueur</TableCell>
            {/* <TableCell align="right">{labels.ratioLabel}</TableCell>
            <TableCell align="right">MJ</TableCell> */}
            <TableCell className="player-ranking-table__head-cell" align="right">{labels.mainLabel}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {data.items.map((item, index) => {
            const rank = (data.page - 1) * data.limit + index + 1;
            const avatar = getRankingAvatar(item);

            return (
              <TableRow key={item.player?._id || `${item._id}-${index}`} hover className="player-ranking-table__row">
                <TableCell className="player-ranking-table__body-cell player-ranking-table__rank-cell">{rank}</TableCell>

                <TableCell className="player-ranking-table__body-cell player-ranking-table__avatar-cell">
                  <Avatar
                    src={avatar.src}
                    alt={avatar.alt}
                    className={`player-ranking-table__avatar player-ranking-table__avatar--${avatar.variant}`}
                  >
                    <PersonIcon fontSize="small" />
                  </Avatar>
                </TableCell>

                <TableCell className="player-ranking-table__body-cell player-ranking-table__player-cell">
                  <Box className="player-ranking-table__player-text">
                    <Typography component="p" className="player-ranking-table__player-name">
                      {renderPlayerName(item)}
                    </Typography>

                    <Typography component="p" className="player-ranking-table__team-name">
                      {renderTeamName(item)}
                    </Typography>
                  </Box>
                </TableCell>

                {/* <TableCell align="right" className="player-ranking-table__number-cell">
                  {formatDecimal(item.valuePerMatch)}
                </TableCell>

                <TableCell align="right" className="player-ranking-table__number-cell">
                  {item.matchesCount ?? '-'}
                </TableCell> */}

                <TableCell align="right" className="player-ranking-table__body-cell player-ranking-table__value-cell">
                  <span>{item.value ?? 0}</span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box className="player-ranking-section">
      {loading ? (
        <Box className="player-ranking-section__loader">
          <CircularProgress size={26} />
        </Box>
      ) : error ? (
        <Typography className="player-ranking-section__feedback player-ranking-section__feedback--error">
          {error}
        </Typography>
      ) : data.items?.length === 0 ? (
        <Typography className="player-ranking-section__feedback">Aucun résultat.</Typography>
      ) : (
        <>
          {renderTable()}

          <Box className="player-ranking-section__footer">
            {data.totalPages > 1 ? (
              <Pagination
                page={page}
                count={data.totalPages}
                onChange={(_, value) => setPage(value)}
                size="small"
                siblingCount={1}
                boundaryCount={1}
                className="player-ranking-pagination"
              />
            ) : (
              <Box />
            )}

            {showLimitSelector ? (
              <FormControl size="small" className="player-ranking-limit-select">
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  renderValue={(value) => `${value} / page`}
                >
                  {limitOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option} / page
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
          </Box>
        </>
      )}
    </Box>
  );
}
